import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { config } from '../config';
import authService from './auth';
import { ApiResponse, ApiError } from '../types';
import { FriendRequest, User } from 'coop-adventure-engine';

class FriendsService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        const hasValidToken = await authService.ensureValidToken();
        if (hasValidToken) {
          const token = authService.getCurrentToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        return response;
      },
      (error: AxiosError<ApiResponse>) => {
        if (error.response?.status === 401) {
          // Handle unauthorized - auth service will handle token refresh
        }
        return Promise.reject(this.transformError(error));
      }
    );
  }

  private transformError(error: AxiosError<ApiResponse>): ApiError {
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    return {
      code: error.response?.status?.toString() || 'UNKNOWN',
      message: error.response?.data?.message || error.message || 'An error occurred',
      details: error.response?.data,
    };
  }

  /**
   * Send a friend request by email address
   */
  async sendFriendRequest(email: string): Promise<{ success: boolean; requestId?: string; error?: string }> {
    try {
      const response = await this.axiosInstance.post<ApiResponse<{ requestId: string }>>('/api/friends/requests', {
        email,
      });
      if (!response.data.data) {
        throw new Error('Failed to send friend request');
      }
      return { success: true, requestId: response.data.data.requestId };
    } catch (error) {
      const apiError = error as AxiosError<ApiResponse>;
      return {
        success: false,
        error: apiError.response?.data?.error?.message || 'Failed to send friend request',
      };
    }
  }

  /**
   * Get pending friend requests for the authenticated user
   */
  async getFriendRequests(): Promise<FriendRequest[]> {
    const response = await this.axiosInstance.get<ApiResponse<FriendRequest[]>>('/api/friends/requests');
    if (!response.data.data || !Array.isArray(response.data.data)) {
      return [];
    }
    // Convert date strings to Date objects
    return response.data.data.map((req) => {
      const result: FriendRequest = {
        ...req,
        createdAt: new Date(req.createdAt),
      };
      if (req.respondedAt) {
        result.respondedAt = new Date(req.respondedAt);
      }
      return result;
    });
  }

  /**
   * Get sent friend requests by the authenticated user
   */
  async getSentFriendRequests(): Promise<FriendRequest[]> {
    const response = await this.axiosInstance.get<ApiResponse<FriendRequest[]>>(
      '/api/friends/requests/sent'
    );
    if (!response.data.data || !Array.isArray(response.data.data)) {
      return [];
    }
    // Convert date strings to Date objects
    return response.data.data.map((req) => {
      const result: FriendRequest = {
        ...req,
        createdAt: new Date(req.createdAt),
      };
      if (req.respondedAt) {
        result.respondedAt = new Date(req.respondedAt);
      }
      return result;
    });
  }

  /**
   * Respond to a friend request (accept or reject)
   */
  async respondToFriendRequest(requestId: string, accepted: boolean): Promise<{ success: boolean; error?: string }> {
    try {
      await this.axiosInstance.post<ApiResponse>(`/api/friends/requests/${requestId}/respond`, {
        accepted,
      });
      return { success: true };
    } catch (error) {
      const apiError = error as AxiosError<ApiResponse>;
      return {
        success: false,
        error: apiError.response?.data?.error?.message || 'Failed to respond to friend request',
      };
    }
  }

  /**
   * Cancel a sent friend request
   */
  async cancelFriendRequest(requestId: string): Promise<{ success: boolean; error?: string }> {
    try {
      await this.axiosInstance.delete<ApiResponse>(`/api/friends/requests/${requestId}`);
      return { success: true };
    } catch (error) {
      const apiError = error as AxiosError<ApiResponse>;
      return {
        success: false,
        error: apiError.response?.data?.error?.message || 'Failed to cancel friend request',
      };
    }
  }

  /**
   * Get friends list for the authenticated user
   */
  async getFriends(): Promise<User> {
    const response = await this.axiosInstance.get<ApiResponse<User>>('/api/friends');
    if (!response.data.data) {
      throw new Error('Failed to get friends list');
    }
    return response.data.data;
  }

  /**
   * Remove a friend
   */
  async removeFriend(friendId: string): Promise<{ success: boolean; error?: string }> {
    try {
      await this.axiosInstance.delete<ApiResponse>(`/api/friends/${friendId}`);
      return { success: true };
    } catch (error) {
      const apiError = error as AxiosError<ApiResponse>;
      return {
        success: false,
        error: apiError.response?.data?.error?.message || 'Failed to remove friend',
      };
    }
  }

  /**
   * Select a friend request and navigate to target scene
   */
  async selectFriendRequest(sessionId: string, playerId: string, requestId: string, targetScene: string): Promise<any> {
    const response = await this.axiosInstance.post<ApiResponse>(`/api/friends/select-request`, {
      sessionId,
      playerId,
      requestId,
      targetScene,
    });
    return response.data;
  }

  /**
   * Select a friend and navigate to target scene
   */
  async selectFriend(sessionId: string, playerId: string, friendId: string, targetScene: string): Promise<any> {
    const response = await this.axiosInstance.post<ApiResponse>(`/api/friends/select-friend`, {
      sessionId,
      playerId,
      friendId,
      targetScene,
    });
    return response.data;
  }
}

// Export singleton instance
const friendsService = new FriendsService();
export default friendsService;

